/**
 * v-click-outside
 *
 * A Vue directive that invokes a provided callback whenever
 * a click event occurs outside the element it is bound to
 *
 * Supports both Vue 2 and Vue 3!
 *
 * # Usage
 * ```vue
 * <template>
 *   <div v-click-outside="onOutsideClick">
 *     <!-- dropdown/popover content -->
 *   </div>
 * </template>
 *
 * <script lang="ts">
 * import clickOutside from '@/lib/directives/clickOutside'
 *
 * export default {
 *   name: 'MyComponent',
 *
 *   directives: { clickOutside },
 *
 *   methods: {
 *    Handler invoked when a click outside the element is detected.
 *    @param event - Native MouseEvent
 *     onOutsideClick(event: MouseEvent) {
 *       this.isDropdownOpen = false
 *     }
 *   }
 * }
 * </script>
 * ```
 */

import { DirectiveOptions, VNode } from 'vue'
import type { ObjectDirective, DirectiveBinding } from 'vue'

/**
 * Extends HTMLElement to store a reference to the click handler for cleanup.
 */
interface ClickOutsideHTMLElement extends HTMLElement {
  /**
   * Internal reference to the bound event handler, used for removal on unbind/unmounted.
   */
  __clickOutsideHandler__?: (event: MouseEvent) => void
}

/**
 * Directive that triggers a handler when clicks occur outside the bound element.
 * Implements both Vue 2 (bind/unbind) and Vue 3 (beforeMount/unmounted) hooks.
 */
const clickOutside = {
  /**
   * Vue 2: Called once, when directive is first bound to the element.
   * Vue 3: Alias for beforeMount, also invoked before mounting.
   * @param el - the element the directive is bound to
   * @param binding - object containing the callback in `binding.value`
   * @param vnode - current VNode
   * @param prevVNode - previous VNode
   */
  bind(
    el: ClickOutsideHTMLElement,
    binding: DirectiveBinding<(e: MouseEvent) => void>,
    vnode: VNode,
    prevVNode: VNode,
  ) {
    const handler = (event: MouseEvent): void => {
      // If click is outside element, call supplied callback
      if (!el.contains(event.target as Node)) {
        binding.value(event)
      }
    }
    el.__clickOutsideHandler__ = handler
    document.addEventListener('click', handler)
  },

  /**
   * Vue 3: Called before the element is mounted.
   * @see bind()
   * @param el - the element the directive is bound to
   * @param binding - object containing the callback
   * @param vnode - current VNode
   * @param prevVNode - previous VNode
   */
  beforeMount(
    el: ClickOutsideHTMLElement,
    binding: DirectiveBinding<(e: MouseEvent) => void>,
    vnode: VNode,
    prevVNode: VNode,
  ) {
    clickOutside.bind(el, binding, vnode, prevVNode)
  },

  /**
   * Vue 2: Called once when directive is unbound from element.
   * @param el - the element the directive was bound to
   * @param binding - directive binding object
   * @param vnode - current VNode
   * @param prevVNode - previous VNode
   */
  unbind(
    el: ClickOutsideHTMLElement,
    binding: DirectiveBinding<(e: MouseEvent) => void>,
    vnode: VNode,
    prevVNode: VNode,
  ) {
    const handler = el.__clickOutsideHandler__

    if (handler) {
      document.removeEventListener('click', handler)
      delete el.__clickOutsideHandler__
    }
  },

  /**
   * Vue 3: Called when the element is unmounted.
   * @see unbind()
   * @param el - the element the directive was bound to
   * @param binding - directive binding object
   * @param vnode - current VNode
   * @param prevVNode - previous VNode
   */
  unmounted(
    el: ClickOutsideHTMLElement,
    binding: DirectiveBinding<(e: MouseEvent) => void>,
    vnode: VNode,
    prevVNode: VNode,
  ) {
    clickOutside.unbind(el, binding, vnode, prevVNode)
  },
} as DirectiveOptions & ObjectDirective<ClickOutsideHTMLElement, (e: MouseEvent) => void>

export default clickOutside
